import { MouseEvent, ReactElement } from 'react';
import { CELL_CLASS_NAMES, ROW_CLASS_NAMES } from '@client/cart/components/Cart';
import { ConfigurableButtons } from '@client/cart/components/Cart/Product/ConfigurableButtons';
import { StockCount } from '@client/common/components/StockCount';
import { Tooltip } from '@client/common/components/Tooltip';
import { useDeviceType } from '@client/common/hooks';
import { isDefined } from '@client/common/utils/boolean/isDefined';
import { useCertificationValidator } from '@client/context/hooks';
import { useTranslate } from '@client/i18n/hooks';
import { getCurrentPriceIndex } from '@client/product/hooks/useProductBulkPrices';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useOpenLink, useUrlCreator } from '@client/routes/hooks';
import { Alert, ALERT_DISPLAY_TYPE, ALERT_STYLE_TYPE } from '@lib/components/Alert';
import { Checkbox } from '@lib/components/Form/Checkbox/Checkbox';
import { Icon, ICON_NAME, ICON_SIZE } from '@lib/components/Icon';
import { PRICE_STYLE_TYPE } from '@lib/components/Price';
import { LabelList } from '@lib/components/ProductList/ProductBox/common/LabelList';
import classNames from 'classnames';
import { PRODUCT_LABEL_SIZE } from 'b2b-components/core/components/ProductLabel/ProductLabel/ProductLabel';
import { useIsEnabled } from 'b2b-components/hooks/useIsEnabled';
import { isMobile } from 'b2b-components/utils/device';
import { Actions } from './Actions';
import { Buttons } from './Buttons';
import { Details } from './Details';
import { HiddenPrice } from './HiddenPrice';
import { Price } from './Price';
import { Quantity } from './Quantity';
import { Reference } from './Reference';
import { Thumbnail } from './Thumbnail';
import { Title } from './Title';
import { Props } from './';
import './Product.scss';

export const Product = ({
  product,
  listPosition,
  isSelectable,
  isEditable,
  hasActions,
  hasConfigurableItemsEnabled,
  isAddToCompareEnabled,
  isReferenceReadOnly,
  isWishlistPage,
  onOpen,
  onSelect,
  onRemove,
  onAddToCart,
  onQuantityChange,
  onReferenceBlur,
  onReferenceChange,
  getIsSelected,
  className,
  showEmptyReferenceField,
}: Props): ReactElement => {
  const t = useTranslate();
  const certificationValidator = useCertificationValidator();
  const urlCreator = useUrlCreator();
  const openLink = useOpenLink();
  const [onEnable, onDisable, isEnabled] = useIsEnabled(false);
  const deviceType = useDeviceType();
  const isTouchScreen = isMobile(deviceType);
  const isHoverableElementVisible = isTouchScreen || isEnabled;

  const {
    sku,
    name,
    cover,
    total,
    single,
    isActive,
    settings,
    certificates,
    cart,
    supplierConfig,
    bulkPrice,
  } = product;

  const { quantity } = cart;
  const currentPriceIndex = getCurrentPriceIndex(bulkPrice, quantity);
  const isProductActive = isActive && (settings?.isBuyable !== false);
  const isDataIncomplete = !name;
  const isUserCertified = certificationValidator(certificates || []);
  const priceDiscount = isDefined(total?.discountWithoutVat) ? -total.discountWithoutVat : null;
  const currency = single?.currency || null;
  const price = single ? isUserCertified ? single.priceWithoutVat : single.price : null;
  const priceTotal = total ? isUserCertified ? total.priceWithoutVat : total.price : null;
  const stockCount = isDefined(settings?.quantity) ? settings.quantity : null;
  const quantityOrdered = settings?.quantityOrdered || null;
  const isSpecialOrder = !!settings?.isSpecialOrder;
  const isDirectDelivery = settings?.isDirectDelivery;
  const productUrl = isProductActive ? urlCreator(ROUTES.product, { sku }) : null;

  const hasConfigurableItemDetails = hasConfigurableItemsEnabled && product.settings?.configurableType;
  const showConfigurableProductButtons = hasConfigurableItemDetails && supplierConfig;
  const productPreviewUrl = supplierConfig?.productPreviewUrl || null;
  const referenceDisabledMessage = hasConfigurableItemDetails ? t('This field transmits necessary item data and is blocked for editing') : undefined;
  const imageUrl = hasConfigurableItemDetails ? productPreviewUrl || cover : cover;
  const isPriceShown = (!isDataIncomplete && isProductActive && currency && isDefined(price) && isUserCertified);
  const moreBulkPrice = Array.isArray(bulkPrice) ? bulkPrice[currentPriceIndex + 1] : null;

  const handleOpen = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isProductActive) {
      event.preventDefault();
      onOpen?.(sku, listPosition);

      if (productUrl) {
        openLink(event, productUrl);
      }
    }
  };

  return (
    <li
      onMouseEnter={onEnable}
      onMouseLeave={onDisable}
      className={classNames(
        'CartProduct',
        {
          [ROW_CLASS_NAMES.withDiscount]: !!priceDiscount,
          [ROW_CLASS_NAMES.withChildren]: showConfigurableProductButtons,
          [ROW_CLASS_NAMES.incompleteData]: isDataIncomplete,
          'CartProduct--Hover': isEnabled,
        },
        className,
      )}
    >
      {isSelectable && (
        <Checkbox
          className={classNames('CartProduct-Checkbox', CELL_CLASS_NAMES.select)}
          isChecked={getIsSelected?.(sku)}
          onChange={() => onSelect?.(sku)}
        />
      )}

      <Thumbnail
        url={imageUrl}
        title={name}
        isActive={isProductActive}
        onClick={handleOpen}
        productUrl={productUrl || undefined}
        className={CELL_CLASS_NAMES.thumbnail}
      />

      {!isDataIncomplete ? (
          <div className={CELL_CLASS_NAMES.title}>
            <Tooltip
              isApplied={!isProductActive}
              content={t('This item is no longer available')}
            >
              <Title
                title={name}
                onClick={handleOpen}
                productUrl={productUrl || undefined}
              >
                <LabelList
                  size={PRODUCT_LABEL_SIZE.small}
                  isCoreAssortment={settings?.isCoreAssortment}
                  isOwnAssortment={settings?.isOwnAssortment}
                  text={{
                    new: t('new flag', {}, 'Product flag icon - "new" label'),
                    occasion: t('verified return flag', {}, 'Product flag icon - "verified return" label'),
                    promotion: t('special offer flag', {}, 'Product flag icon - "special offer" label'),
                    sellout: t('sale flag', {}, 'Product flag icon - "sale" label'),
                    coreAssortment: t('Core assortment'),
                    ownAssortment: t('Customer specific assortment'),
                    consciousAlternative: t('Conscious alternative'),
                  }}
                  className='CartProduct-LabelList'
                />
              </Title>
            </Tooltip>
          </div>
      )
        : (
          <div className={`${CELL_CLASS_NAMES.incompleteData} CartProduct-IncompleteDataWrapper`}>
            <Alert
              displayType={ALERT_DISPLAY_TYPE.text}
              styleType={ALERT_STYLE_TYPE.warning}
              className='CartProduct-IncompleteDataAlert'
            >
              <span>{t('This item cannot be displayed due to incomplete data')}</span>
              {!!sku && (<span>({t('Art.-Nr.')} {sku})</span>)}
            </Alert>
          </div>
        )}

      {(isProductActive && currency && !!priceDiscount) && (
        <>
          <span className={classNames('CartProduct-DiscountLabel', CELL_CLASS_NAMES.discountLabel)}>
            {t('Discount applied')}:
          </span>
          <Price
            label={t('Discount applied')}
            price={priceDiscount}
            currency={currency}
            styleType={PRICE_STYLE_TYPE.discount}
            className={classNames('CartProduct-Discount', CELL_CLASS_NAMES.discount)}
            labelClassName='CartProduct-DiscountLabel'
          />
        </>
      )}

      {(!isDataIncomplete && isProductActive && isDefined(stockCount)) && (
        <StockCount
          stockCount={stockCount}
          isSpecialOrder={isSpecialOrder}
          directDelivery={isDirectDelivery}
          quantityOrdered={quantityOrdered}
          className={classNames('CartProduct-StockCount', CELL_CLASS_NAMES.stock)}
          tooltipClassName='CartProduct-StockCountTooltip'
        />
      )}

      {(isPriceShown) && (
        <Price
          price={price}
          currency={currency}
          label={t('Price')}
          className={CELL_CLASS_NAMES.price}
        />
      )}

      {(!isDataIncomplete && isProductActive && !isUserCertified) && (
        <HiddenPrice
          label={t('Price')}
          className={CELL_CLASS_NAMES.price}
        />
      )}

      {!isDataIncomplete && isProductActive && (
        <Quantity
          sku={sku}
          quantity={quantity}
          isEditable={isEditable}
          onChange={(quantity) => onQuantityChange?.(quantity, product, listPosition)}
          className={CELL_CLASS_NAMES.quantity}
        />
      )}

      {isEditable && (
        <Buttons
          sku={sku}
          isVisible={isHoverableElementVisible}
          onRemove={() => onRemove?.(product, listPosition)}
          className={CELL_CLASS_NAMES.buttons}
        />
      )}

      {(isPriceShown) && (
        <Price
          price={priceTotal || undefined}
          currency={currency}
          label={t('Sum')}
          className={classNames('CartProduct-PriceTotal', CELL_CLASS_NAMES.total)}
        />
      )}

      {(!isDataIncomplete && isProductActive && !isUserCertified) && (
        <HiddenPrice
          label={t('Sum')}
          className={CELL_CLASS_NAMES.total}
        />
      )}

      {(!isDataIncomplete && isProductActive && hasActions) && (
        <Actions
          product={product}
          isUserCertified={isUserCertified}
          isAddToCompareEnabled={isAddToCompareEnabled}
          areActionsVisible={isEnabled}
          onAddToCart={() => onAddToCart?.(sku, listPosition)}
          className={CELL_CLASS_NAMES.actions}
        />
      )}

      {!isDataIncomplete && (
        <Details
          isProductActive
          product={product}
          className={classNames('CartProduct-Details', CELL_CLASS_NAMES.details)}
        />
      )}

      {(!isDataIncomplete && showConfigurableProductButtons) && (
        <ConfigurableButtons
          product={product}
          className={CELL_CLASS_NAMES.children}
        />
      )}

      {!isDataIncomplete && (
        <div className={classNames(CELL_CLASS_NAMES.reference, 'CartProduct-ReferenceRow')}>
          <Reference
            product={product}
            isEditable={!!isEditable}
            isReadOnly={!!isReferenceReadOnly}
            isProductActive={isProductActive}
            isWishlistPage={isWishlistPage}
            onBlur={onReferenceBlur}
            onChange={onReferenceChange}
            disabledMessage={referenceDisabledMessage}
            className='CartProduct-Reference'
            showEmptyReferenceField={showEmptyReferenceField}
          />
          {(isProductActive && hasActions) && (
            <Actions
              product={product}
              isUserCertified={isUserCertified}
              isAddToCompareEnabled={isAddToCompareEnabled}
              areActionsVisible={isHoverableElementVisible}
              onAddToCart={() => onAddToCart?.(sku, listPosition)}
              className='CartProduct-ReferenceActions'
            />
          )}
        </div>
      )}
      {(!!bulkPrice?.length && currentPriceIndex !== -1) && (<div className='CartProduct-BulkItem'>
        <Icon name={ICON_NAME.bulk}
              size={ICON_SIZE.medium}
              className='CartProduct-BulkItem-Icon' />
        <span className='CartProduct-BulkItem-TextUpper'>
            {t('Bulk pricing')}
          </span>
        {moreBulkPrice ? <div className='CartProduct-BulkItemText'>
          {t(
            'Buy {more} more to save CHF',
            {
              more: (moreBulkPrice
                ? moreBulkPrice.minQuantity
                : (quantity + 1)
              ) - quantity,
            },
          )}
          &nbsp;
          <span className='CartProduct-BulkItem--Bold'>
            {((single?.priceWithoutVat || 0) - (moreBulkPrice
              ? moreBulkPrice.priceWithoutVat
              : (bulkPrice[currentPriceIndex]?.priceWithoutVat || 0)
            )).toFixed(2)}
          </span>
          &nbsp;
          <span className='CartProduct-BulkItem-TextLower'>
            {t('per item')}
          </span>
        </div> : null}
      </div>)}
    </li>
  );
};
